import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import AppStack from '../../common/AppStack';
import { singularPluralFormat } from '../../../helpers/format';
import { Context as VendorContext } from '../../../providers/VendorContextProvider';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import AppText from '../../common/AppText';
import { triggerNotification } from '../../../helpers/notification';
import ResourceSelectModal from '../../common/ResourceSelectModal';

const DeleteProductsModal = ({
  isOpen,
  onClose,
  fkEcomVendor,
  fkEcomStore,
  products,
  filter,
  totalItemCount,
  onSuccess
}) => {
  const [formState, setFormState] = useState({
    selectType: '',
    loading: false
  });
  const {
    state: vendorState,
    fetchVendorProductOptionsSummary,
    deleteVendorProducts
  } = useContext(VendorContext);
  const {
    state: storeState,
    fetchStoreProductOptionsSummary,
    deleteStoreProducts
  } = useContext(StoreContext);
  const productsToDelete =
    (formState.selectType === 'all'
      ? fkEcomVendor
        ? vendorState.ecomVendorProductSummaryOptions.value?.ecomVendorProducts
        : storeState.ecomStoreProductSummaryOptions.value?.ecomStoreProducts
      : products) || [];

  useEffect(() => {
    if (isOpen) {
      setFormState({
        selectType: '',
        loading: false
      });
    }
  }, [isOpen]);

  const onSuccessCallback = () => {
    triggerNotification(
      `${productsToDelete.length === 1 ? 'Product' : 'Products'} deleted.`,
      'success'
    );
    onClose();
    if (onSuccess) {
      onSuccess();
    }
  };

  const onErrorCallback = (e) => {
    setFormState({ ...formState, loading: false });
    triggerNotification(e);
  };

  return (
    <ResourceSelectModal
      filter={filter}
      formSectionProps={{
        onCancel: onClose,
        isLoading: formState.loading,
        onSubmit: () => {
          setFormState({ ...formState, loading: true });
          if (fkEcomVendor) {
            const productIds = productsToDelete.map(
              (p) => p.pkEcomVendorProduct
            );

            deleteVendorProducts(
              fkEcomVendor,
              productIds,
              onSuccessCallback,
              onErrorCallback
            );
          }
          else {
            const productIds = productsToDelete.map(
              (p) => p.pkEcomStoreProduct
            );

            deleteStoreProducts(
              fkEcomStore,
              productIds,
              onSuccessCallback,
              onErrorCallback
            );
          }
        },
        submitTitle: `Yes, delete ${singularPluralFormat(
          productsToDelete.length,
          'product',
          'products'
        )}`
      }}
      isLoading={
        storeState.ecomStoreProductSummaryOptions.loading ||
        vendorState.ecomVendorProductSummaryOptions.loading
      }
      isOpen={isOpen}
      onClose={onClose}
      onSelect={(selectType) => {
        setFormState({ ...formState, selectType });
        if (selectType === 'all') {
          if (fkEcomVendor) {
            fetchVendorProductOptionsSummary(
              fkEcomVendor,
              filter,
              null,
              triggerNotification
            );
          }
          else {
            fetchStoreProductOptionsSummary(
              fkEcomStore,
              filter,
              null,
              triggerNotification
            );
          }
        }
      }}
      selectedIds={products.map((p) => p.uuid)}
      selectLabel="Delete"
      selectLabelDescription="Select the products you want to delete."
      size={450}
      title="Delete products?"
      totalItemCount={totalItemCount}
    >
      <AppStack style={{ gap: 8 }}>
        <AppStack style={{ gap: 10 }}>
          <AppText style={{ fontSize: 14 }}>
            Are you sure you want to delete{' '}
            <span style={{ fontWeight: 500 }}>
              {productsToDelete?.length === 1
                ? productsToDelete[0].ecomVendorProduct?.name ||
                  productsToDelete[0].name
                : `${productsToDelete?.length} products`}
            </span>
            ?
          </AppText>
          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
            This action cannot be undone.
          </AppText>
        </AppStack>
      </AppStack>
    </ResourceSelectModal>
  );
};

DeleteProductsModal.propTypes = {
  filter: PropTypes.object,
  fkEcomStore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fkEcomVendor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  products: PropTypes.array,
  totalItemCount: PropTypes.number
};

export default DeleteProductsModal;
